import React, {Fragment, useEffect, useState} from "react";
import clsx from "clsx";
import {capitalize, includes} from "lodash";
import {Link, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useFetchMessages, useFetchSentList} from "hooks";
import {BufferBar, DrawerList, Messages} from "components";
import {setNewMessageDialog, setTabIndex} from "redux/actions";
//Material UI Components
import {Alert, Skeleton} from '@material-ui/lab';
import AddIcon from "@material-ui/icons/Add";
import {AppBar, Avatar, Box, Collapse, Fab, Grid, Hidden, IconButton, Tab, Tabs, Typography} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {useGmailTabItemStyles, useGmailTabsStyles} from "styles/gmailTabStyle";
import {ExpandLess, ExpandMore} from "@material-ui/icons";

const TabPanel = ({children}) => (
    <Fragment>
        <Box>
            {children}
        </Box>
    </Fragment>
);

const useStyles = makeStyles(() => ({
    root: {
        paddingLeft: '1px',
        flexGrow: 1,
        backgroundColor: "#efefef"
    },
    positionSticky: {
        top: '5.5em',
        borderBottom: '1px solid lightgray',
        borderTop: '1px solid lightgray'
    },
    tabContent: {
        flex: 1
    },
    skeletonContainer: {
        paddingTop: '.5em',
        backgroundColor: '#FFF'
    },
    noFoundText: {
        backgroundColor: '#FFF', padding: 10
    },
    composeButton: {
        position: 'fixed', bottom: '3em', right: '3em', padding: '0 1.5em'
    },
    ml1: {
        marginLeft: '0.5em'
    },
    textLeft: {
        textAlign: 'left'
    }
}));

const MessagesList = ({history}) => {
    const {tab} = useParams();
    const classes = useStyles();
    const dispatch = useDispatch();

    const [fetchingMessages, messages] = useFetchMessages();
    const [fetchSentList, fetchingSentList, _sentList] = useFetchSentList()

    const [sentList, setSentList] = useState(false);
    const [expandDisclaimer, setExpandDisclaimer] = useState(false)

    const {inbox} = useSelector(state => state);
    const {tabIndex, tabs, member_type, paidUser} = inbox;

    const handleChange = (event, index) => dispatch(setTabIndex(index));
    const handleExpandDisclaimer = () => setExpandDisclaimer(prevState => !prevState);

    const handleSentList = (open, ids = []) => (event) => {
        event.preventDefault();
        setSentList(open);
        if (ids?.length > 0)
            fetchSentList(ids);
    };

    const openNewMessageDialog = () => dispatch(setNewMessageDialog(true));

    const indicatorColors = ['#d93025', '#1a73e8', '#188038'];

    const tabsStyles = useGmailTabsStyles({indicatorColors});

    const tabStyle = {
        tabItemStyles1: useGmailTabItemStyles({color: indicatorColors[0]}),
        tabItemStyles2: useGmailTabItemStyles({color: indicatorColors[1]}),
        tabItemStyles3: useGmailTabItemStyles({color: indicatorColors[2]}),
    }

    useEffect(() => {
        const initialRoute = () => history.push(tabs[0].slug);
        if (tab === 'inbox')
            initialRoute()
        else {
            const currIndex = tabs.findIndex(_tab => _tab.slug === tab);
            handleChange(null, currIndex)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, history]);

    return (
        <div className={clsx({[classes.root]: !fetchingMessages})}>
            <Hidden xsDown>
                {includes(["inbox", "primary", "verified", "anonymous"], tab) && (
                    <AppBar position={'sticky'} classes={{positionSticky: classes.positionSticky}} color="default"
                            style={{zIndex: 1300}} elevation={0}>
                        <Tabs classes={tabsStyles}
                              value={tabIndex}
                              onChange={handleChange}
                              indicatorColor="primary"
                              textColor="primary"
                              TabIndicatorProps={{
                                  children: <div className={`MuiIndicator-${tabIndex}`}/>,
                              }}>
                            {/*Dynamic Labels Map*/}
                            {tabs?.length > 0 &&
                                tabs.map(({label, id, icon, slug, tag_line, total}, i) => (
                                    <Tab key={id}
                                         classes={tabStyle['tabItemStyles' + (i + 1)]}
                                         disableTouchRipple
                                         label={<div className={'MuiTabItem-labelGroup'}>
                                             <div className={'MuiTabItem-label'}>
                                                 <span> {label} </span>
                                                 {total > 0 && <span className={'MuiTabItem-tag'}> {total} New </span>}
                                             </div>
                                             <Hidden smDown><p style={{textAlign: 'left'}}>{tag_line}</p></Hidden>
                                         </div>}
                                         icon={<span dangerouslySetInnerHTML={{__html: icon}}/>}
                                         component={Link} to={`/${slug}`}/>
                                ))}
                        </Tabs>
                    </AppBar>
                )}
            </Hidden>
            <TabPanel className={classes.tabContent} value={tabIndex} index={tabIndex}>
                <Fragment>
                    {includes(["inbox", "primary", "verified", "anonymous"], tab) &&
                        <Alert severity="warning">
                            <Grid item container direction='column' alignContent='center' justify='center'>
                                <Grid item> <strong>DISCLAIMER:</strong>
                                    <IconButton size='small' onClick={handleExpandDisclaimer}>
                                        {expandDisclaimer
                                            ? <ExpandLess/>
                                            : <ExpandMore/>
                                        }
                                    </IconButton>
                                </Grid>
                                <Collapse in={expandDisclaimer}>
                                    <Grid item> {tabs?.[tabIndex]?.description} </Grid>
                                </Collapse>
                            </Grid>
                        </Alert>
                    }
                </Fragment>
                {
                    fetchingMessages ? (<BufferBar/>)
                        && Array(9).fill(0).map((num, index) => (
                            <div key={index}>
                                <Hidden xsDown>
                                    <Grid className={classes.skeletonContainer} container
                                          alignItems={'center'}>
                                        <Grid item className={classes.ml1}>
                                            <Skeleton variant="rect" width={20} height={20}/>
                                        </Grid>
                                        <Grid item className={classes.ml1}>
                                            <Skeleton variant="rect" width={20} height={20}/>
                                        </Grid>
                                        <Grid item className={classes.ml1}>
                                            <Skeleton variant="rect" width={20} height={20}/>
                                        </Grid>
                                        <Grid item className={classes.ml1}>
                                            <Skeleton variant="circle" width={35} height={35}>
                                                <Avatar/></Skeleton>
                                        </Grid>
                                        <Grid item xs container className={classes.ml1}>
                                            <Skeleton style={{width: '100%'}} variant="rect" height={25}/>
                                        </Grid>
                                    </Grid>
                                </Hidden>
                                <Hidden smUp>
                                    <Grid key={index}
                                          className={classes.skeletonContainer}
                                          container
                                          alignItems={'center'}>
                                        <Grid item style={{paddingTop: '1em'}}>
                                            <Skeleton variant="circle" width={40} height={40}><Avatar/></Skeleton>
                                        </Grid>
                                        <Grid item container xs direction={'column'} style={{marginTop: '0.75em'}}>
                                            <Grid item>
                                                <Skeleton style={{width: '40%'}} variant="rect" height={10}/>
                                            </Grid>
                                            <Grid item style={{marginTop: '5px'}}>
                                                <Skeleton style={{width: '100%'}} variant="rect" height={20}/>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Hidden>
                            </div>
                        ))
                        : messages?.length > 0 ?
                            (messages.map((message, i) =>
                                <Messages key={i} message={message} memberType={member_type} handleList={handleSentList}
                                          history={history} paidUser={paidUser}/>))
                            : (<Grid container justify='center' align='center' className={classes.noFoundText}>
                                <Typography align='center'> No Message Available in {capitalize(tab)} </Typography>
                            </Grid>)}
                <Fab variant="extended" className={classes.composeButton} size="small"
                     aria-label="extended" color="primary" onClick={openNewMessageDialog}>
                    <AddIcon/>
                    Compose
                </Fab>
            </TabPanel>
            <DrawerList open={sentList} close={handleSentList(false)} list={_sentList} loading={fetchingSentList}/>
        </div>
    );
}
export default MessagesList;
